import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Mail, Users, TrendingUp, Gift, Calendar } from 'lucide-react';
import { toast } from 'sonner';

interface Campaign {
  id: string;
  campaign_name: string;
  campaign_type: string;
  target_audience: string;
  message_title: string;
  message_body: string;
  incentive_type: string | null;
  incentive_value: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  total_recipients: number;
  total_engaged: number;
  engagement_rate: number;
}

export default function ReengagementCampaigns() {
  const { user } = useInternalAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [campaignName, setCampaignName] = useState('');
  const [campaignType, setCampaignType] = useState<string>('');
  const [targetAudience, setTargetAudience] = useState<string>('');
  const [messageTitle, setMessageTitle] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [incentiveType, setIncentiveType] = useState<string>('');
  const [incentiveValue, setIncentiveValue] = useState('0');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    loadCampaigns();
  }, [user, navigate]);

  const loadCampaigns = async () => {
    try {
      const { data, error } = await supabase
        .from('re_engagement_campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!campaignName || !campaignType || !targetAudience || !messageTitle || !messageBody || !startDate || !endDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { error } = await supabase
        .from('re_engagement_campaigns')
        .insert({
          campaign_name: campaignName,
          campaign_type: campaignType,
          target_audience: targetAudience,
          message_title: messageTitle,
          message_body: messageBody,
          incentive_type: incentiveType || null,
          incentive_value: parseInt(incentiveValue),
          start_date: startDate,
          end_date: endDate,
          created_by: user?.id
        });

      if (error) throw error;

      toast.success('Campaign created successfully!');
      resetForm();
      setShowForm(false);
      loadCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    }
  };

  const resetForm = () => {
    setCampaignName('');
    setCampaignType('');
    setTargetAudience('');
    setMessageTitle('');
    setMessageBody('');
    setIncentiveType('');
    setIncentiveValue('0');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-6 mb-4">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Re-engagement Campaigns</h1>
              <p className="text-muted-foreground mt-1">
                Create campaigns to re-engage inactive users and boost retention
              </p>
            </div>
            <Button onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Cancel' : 'New Campaign'}
            </Button>
          </div>

          {showForm && (
            <Card>
              <CardHeader>
                <CardTitle>Create New Campaign</CardTitle>
                <CardDescription>
                  Design a campaign to bring users back to the platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateCampaign} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="campaignName">Campaign Name</Label>
                      <Input
                        id="campaignName"
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                        placeholder="e.g., Spring Return Bonus"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="campaignType">Campaign Type</Label>
                      <Select value={campaignType} onValueChange={setCampaignType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="off_season">Off Season</SelectItem>
                          <SelectItem value="inactive_user">Inactive User</SelectItem>
                          <SelectItem value="new_season">New Season</SelectItem>
                          <SelectItem value="special_offer">Special Offer</SelectItem>
                          <SelectItem value="milestone">Milestone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="targetAudience">Target Audience</Label>
                      <Select value={targetAudience} onValueChange={setTargetAudience}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select audience" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Users</SelectItem>
                          <SelectItem value="coaches">Coaches</SelectItem>
                          <SelectItem value="teams">Teams</SelectItem>
                          <SelectItem value="venues">Venues</SelectItem>
                          <SelectItem value="players">Players</SelectItem>
                          <SelectItem value="inactive">Inactive Users</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="incentiveType">Incentive Type</Label>
                      <Select value={incentiveType} onValueChange={setIncentiveType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select incentive" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="points">Points</SelectItem>
                          <SelectItem value="discount">Discount</SelectItem>
                          <SelectItem value="free_feature">Free Feature</SelectItem>
                          <SelectItem value="badge">Badge</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="messageTitle">Message Title</Label>
                    <Input
                      id="messageTitle"
                      value={messageTitle}
                      onChange={(e) => setMessageTitle(e.target.value)}
                      placeholder="Catchy title for your message"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="messageBody">Message Body</Label>
                    <Textarea
                      id="messageBody"
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      placeholder="Write your message here..."
                      rows={4}
                      required
                    />
                  </div>

                  {incentiveType && incentiveType !== 'none' && (
                    <div>
                      <Label htmlFor="incentiveValue">Incentive Value</Label>
                      <Input
                        id="incentiveValue"
                        type="number"
                        value={incentiveValue}
                        onChange={(e) => setIncentiveValue(e.target.value)}
                        placeholder="e.g., 100 points, 20% discount"
                      />
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="startDate">Start Date</Label>
                      <Input
                        id="startDate"
                        type="datetime-local"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="endDate">End Date</Label>
                      <Input
                        id="endDate"
                        type="datetime-local"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full">
                    Create Campaign
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {campaigns.map((campaign) => (
              <Card key={campaign.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base">{campaign.campaign_name}</CardTitle>
                    <Badge variant={campaign.is_active ? 'default' : 'secondary'}>
                      {campaign.is_active ? 'Active' : 'Ended'}
                    </Badge>
                  </div>
                  <CardDescription className="capitalize">
                    {campaign.campaign_type.replace(/_/g, ' ')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="capitalize">{campaign.target_audience}</span>
                  </div>
                  
                  {campaign.incentive_type && campaign.incentive_type !== 'none' && (
                    <div className="flex items-center gap-2 text-sm">
                      <Gift className="w-4 h-4 text-primary" />
                      <span className="capitalize">
                        {campaign.incentive_type.replace(/_/g, ' ')} - {campaign.incentive_value}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {new Date(campaign.start_date).toLocaleDateString()} - 
                      {new Date(campaign.end_date).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <div className="pt-3 border-t grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-2xl font-bold">{campaign.total_recipients}</div>
                      <div className="text-xs text-muted-foreground">Recipients</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold flex items-center">
                        {campaign.engagement_rate.toFixed(1)}%
                        <TrendingUp className="w-4 h-4 ml-1 text-green-600" />
                      </div>
                      <div className="text-xs text-muted-foreground">Engagement</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {campaigns.length === 0 && !loading && (
            <Card>
              <CardContent className="p-12 text-center">
                <Mail className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first re-engagement campaign to bring users back
                </p>
                <Button onClick={() => setShowForm(true)}>
                  Create Campaign
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
